/**
 * Password Expired Page
 * Forced gate shown instead of the dashboard when the current user's
 * password has passed the 30-day expiry policy. See ProtectedRoute.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { authApi } from '@/api/auth'
import { toast } from '@/lib/toast'
import { PasswordChangeForm } from '@/pages/profile/PasswordChangeForm'

export function PasswordExpired() {
  const navigate = useNavigate()
  const { refreshUser } = useAuth()
  const [isWaiving, setIsWaiving] = useState(false)

  const handleChanged = async () => {
    await refreshUser()
    navigate('/', { replace: true })
  }

  const handleRemindLater = async () => {
    setIsWaiving(true)
    try {
      await authApi.waivePasswordExpiry()
      await refreshUser()
      navigate('/', { replace: true })
    } catch (error) {
      console.error('Failed to snooze password expiry warning:', error)
      toast.error('Something went wrong', 'Please try again.')
    } finally {
      setIsWaiving(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-gold-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
          {/* Header with PAO Green */}
          <div className="bg-primary px-8 py-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-lg">
                <span className="text-primary text-3xl font-bold font-heading">PAO</span>
              </div>
            </div>
            <h1 className="text-white text-2xl font-heading font-bold">
              Public Attorney's Office
            </h1>
          </div>

          {/* Content */}
          <div className="px-8 py-8">
            <h2 className="text-2xl font-heading font-semibold text-navy mb-2">
              Your password has expired
            </h2>
            <p className="text-gray-600 mb-6 text-sm">
              For your account's security, please change your password. You can
              also remind yourself later if you're in the middle of something.
            </p>

            <PasswordChangeForm
              submitLabel="Change Password"
              onSuccess={handleChanged}
              onCancel={handleRemindLater}
              cancelLabel={isWaiving ? 'Please wait...' : 'Remind Me Later'}
              cancelDisabled={isWaiving}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default PasswordExpired
